import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SlidersHorizontal, FloppyDisk, ArrowCounterClockwise, CaretDown, CaretUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtErr } from "../../lib/api";
import { Button } from "../ui/button";

const color = (s) => (s >= 90 ? "var(--c-green)" : s >= 70 ? "var(--c-accent)" : s >= 40 ? "var(--c-amber)" : "var(--c-red)");
const delta = (a, b) => (a == null || b == null ? null : Math.round(a - b));

/** What-if: move a design knob and watch the health score and every domain re-score, before
 *  anything is written. Commit applies the trial; Reset throws it away. */
export default function WhatIfBar({ projectId, industry, health, onCommitted }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState([]);
  const [base, setBase] = useState({});
  const [trial, setTrial] = useState({});
  const [specs, setSpecs] = useState({});
  const [specMeta, setSpecMeta] = useState([]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!projectId || !open) return;
    api.get(`/design/${projectId}`).then(({ data }) => {
      setMeta(data.inputs_meta || []);
      setSpecMeta((data.spec_meta || []).slice(0, 6));
      setBase(data.inputs || {});
      setTrial(data.inputs || {});
      setSpecs({});
    }).catch((e) => toast.error(fmtErr(e)));
  }, [projectId, open]);

  const score = useCallback((inputs, sp) => {
    setBusy(true);
    api.post(`/validation/${projectId}/what-if`, { inputs, specs: sp })
      .then(({ data }) => setRes(data))
      .catch((e) => toast.error(fmtErr(e), { duration: 6000 }))
      .finally(() => setBusy(false));
  }, [projectId]);

  const nudge = (key, value) => {
    const next = { ...trial, [key]: Number(value) };
    setTrial(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => score(next, specs), 350);
  };

  const nudgeSpec = (key, value) => {
    const next = { ...specs, [key]: value === "" ? undefined : Number(value) };
    if (next[key] === undefined) delete next[key];
    setSpecs(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => score(trial, next), 450);
  };

  const dirty = useMemo(() => Object.keys(trial).some((k) => Number(trial[k]) !== Number(base[k]))
    || Object.keys(specs).length > 0, [trial, base, specs]);

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post(`/validation/${projectId}/what-if/commit`,
        { inputs: trial, specs });
      onCommitted?.(data);
      toast.success(`Committed — health score ${data.health_score}`);
      setRes(null);
      setBase(trial);
      setSpecs({});
    } catch (e) { toast.error(fmtErr(e), { duration: 8000 }); }
    finally { setCommitting(false); }
  };

  const reset = () => { setTrial(base); setSpecs({}); setRes(null); };
  const shown = res?.health_score ?? health;

  return (
    <div className="mt-4 rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] shadow-sm"
      data-testid="what-if-bar">
      <button data-testid="what-if-toggle" onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left">
        <SlidersHorizontal size={14} className="text-[var(--c-accent)]" />
        <span className="text-[12.5px] text-[var(--c-text)]">What-if — try a design before you commit it</span>
        {res && (
          <span className="font-mono text-[11px]" data-testid="what-if-score"
            style={{ color: color(shown) }}>
            {shown}
            {delta(res.health_score, res.health_before) != null && (
              <span className="ml-1 text-[var(--c-muted)]">
                ({delta(res.health_score, res.health_before) > 0 ? "+" : ""}
                {delta(res.health_score, res.health_before)})
              </span>
            )}
          </span>
        )}
        {busy && <span className="font-mono text-[10px] tracking-widest text-[var(--c-accent)]">SCORING…</span>}
        <span className="ml-auto text-[var(--c-muted)]">{open ? <CaretUp size={12} /> : <CaretDown size={12} />}</span>
      </button>

      {open && (
        <div className="grid grid-cols-1 gap-4 border-t border-[var(--c-border)] p-4 lg:grid-cols-[320px_1fr]">
          <div>
            <div className="mb-2 font-mono text-[9px] uppercase tracking-widest text-[var(--c-muted)]">
              Design knobs · {industry?.replace(/_/g, " ") || "pack"}
            </div>
            {meta.map((s) => (
              <div key={s.key} className="mb-3" data-testid={`what-if-row-${s.key}`}>
                <div className="mb-1 flex justify-between text-[11px]">
                  <span className="text-[var(--c-muted)]">{s.label}</span>
                  <span className="font-mono text-[var(--c-accent)]">
                    {trial[s.key]}{s.unit && ` ${s.unit}`}
                    {Number(trial[s.key]) !== Number(base[s.key]) && (
                      <span className="ml-1 text-[var(--c-muted)]">was {base[s.key]}</span>
                    )}
                  </span>
                </div>
                <input type="range" data-testid={`what-if-slider-${s.key}`} min={s.min} max={s.max}
                  step={s.step} value={trial[s.key] ?? s.min}
                  onChange={(e) => nudge(s.key, e.target.value)}
                  className="w-full accent-[var(--c-accent)]" />
              </div>
            ))}
            {!!specMeta.length && (
              <>
                <div className="mb-2 mt-4 border-t border-[var(--c-border)] pt-3 font-mono text-[9px] uppercase tracking-widest text-[var(--c-muted)]">
                  Trial spec values
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {specMeta.map((s) => (
                    <div key={s.key} className="space-y-1">
                      <label className="text-[10px] text-[var(--c-muted)]">
                        {s.label}{s.unit && <span className="font-mono"> ({s.unit})</span>}
                      </label>
                      <input data-testid={`what-if-spec-${s.key}`} type="number" step="any"
                        value={specs[s.key] ?? ""} placeholder="as specified"
                        onChange={(e) => nudgeSpec(s.key, e.target.value)}
                        className="h-7 w-full rounded-sm border border-[var(--c-border)] bg-[var(--c-bg)] px-2 font-mono text-[11px] text-[var(--c-text)]" />
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" data-testid="what-if-commit" disabled={!dirty || committing}
                onClick={commit}
                className="h-7 rounded-sm bg-[var(--c-green)] text-[10px] text-white hover:bg-[var(--c-green)]/85 disabled:opacity-40">
                <FloppyDisk size={11} className="mr-1" /> {committing ? "Committing…" : "Commit these inputs"}
              </Button>
              <Button size="sm" variant="outline" data-testid="what-if-reset" disabled={!dirty}
                onClick={reset}
                className="h-7 rounded-sm border-[var(--c-border)] text-[10px] text-[var(--c-text)] disabled:opacity-40">
                <ArrowCounterClockwise size={11} className="mr-1" /> Reset
              </Button>
              <span className="self-center text-[10.5px] text-[var(--c-muted)]">
                Nothing is written until you commit.
              </span>
            </div>
          </div>

          <div data-testid="what-if-result">
            {!res && (
              <div className="rounded-sm border border-dashed border-[var(--c-border)] p-4 text-[12px] text-[var(--c-muted)]">
                Move a knob — the score and every domain re-score here against the trial design.
              </div>
            )}
            {res && (
              <>
                <div className="mb-3 flex flex-wrap items-baseline gap-3">
                  <span className="font-mono text-3xl" style={{ color: color(res.health_score) }}>
                    {res.health_score}
                  </span>
                  <span className="font-mono text-[11px] text-[var(--c-muted)]">
                    was {res.health_before ?? "—"} · {res.counts.critical} critical ·{" "}
                    {res.counts.major} major · {res.counts.minor} minor
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {res.domains.map((d) => {
                    const dv = delta(d.score, d.before);
                    return (
                      <div key={d.id} data-testid={`what-if-domain-${d.id}`}
                        className="flex items-center gap-2 rounded-sm border border-[var(--c-border)] px-2 py-1.5">
                        <span className="min-w-0 flex-1 truncate text-[11.5px] capitalize text-[var(--c-text)]">
                          {d.name || d.id.replace(/_/g, " ")}
                        </span>
                        <span className="font-mono text-[11px]" style={{ color: color(d.score) }}>{d.score}</span>
                        {dv != null && dv !== 0 && (
                          <span className="font-mono text-[10px]"
                            style={{ color: dv > 0 ? "var(--c-green)" : "var(--c-red)" }}>
                            {dv > 0 ? "+" : ""}{dv}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {!!res.would_clear.length && (
                  <div className="mt-3 font-mono text-[11px] text-[var(--c-green)]" data-testid="what-if-would-clear">
                    would clear: {res.would_clear.join(", ")}
                  </div>
                )}
                {!!res.would_open.length && (
                  <div className="mt-1 font-mono text-[11px] text-[var(--c-amber)]" data-testid="what-if-would-open">
                    would open: {res.would_open.join(", ")}
                  </div>
                )}
                {!res.would_clear.length && !res.would_open.length && (
                  <div className="mt-3 text-[11.5px] text-[var(--c-muted)]">
                    Same set of open checks — this trial only moves the margins.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
